// components/checkout/ReferralModal.jsx
import { useState } from "react";
import { X, Gift, Loader2, CheckCircle2 } from "lucide-react";
import logo from "../../assets/Revibelogo.png";
import { getAuthToken } from "../../api/auth";

const API_BASE = process.env.REACT_APP_API_URL;

const ReferralModal = ({
  isOpen,
  onClose,
  referralCode,
  setReferralCode,
  subtotal,            // pass the order subtotal so we can compute discount
  onSuccess,           // callback({ referralCode, discountAmount, referrerId, referrerName })
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");
  const [applied, setApplied] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [referrerName, setReferrerName] = useState("");

  if (!isOpen) return null;

  const handleApply = async () => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      setError("Please enter a referral code.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const token = getAuthToken();
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // ── Step 1: validate referral code ──────────────────────────────────────
      const validateRes = await fetch(`${API_BASE}orders/validate-referral`, {
        method: "POST",
        headers,
        body: JSON.stringify({ referralCode: code }),
      });

      const validateData = await validateRes.json();

      if (!validateRes.ok || !validateData.valid) {
        setError(validateData.message || "Invalid referral code.");
        return;
      }

      // ── Step 2: fetch discount config & compute discount ─────────────────────
      const discountRes = await fetch(`${API_BASE}orders/apply-discount`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          referralCode: code,
          amount: subtotal,
        }),
      });

      const discountData = await discountRes.json();

      if (!discountRes.ok || !discountData.success) {
        setError(discountData.message || "Could not apply referral discount.");
        return;
      }

      // Compute buyer discount from returned config
      const { buyerDiscount } = discountData.data || {};
      let computed = 0;
      if (buyerDiscount?.value > 0) {
        computed =
          buyerDiscount.type === "percentage"
            ? Math.round((subtotal * buyerDiscount.value) / 100)
            : buyerDiscount.value;
        computed = Math.min(computed, subtotal); // never exceed subtotal
      }

      setDiscountAmount(computed);
      setReferrerName(validateData.referrerName || "");
      setApplied(true);

      onSuccess?.({
        referralCode: code,
        discountAmount: computed,
        referrerId: validateData.referrerId,
        referrerName: validateData.referrerName,
      });

      setTimeout(() => {
        setApplied(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("[ReferralModal] Network error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError("");
    setApplied(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl p-8 bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={handleClose}
          disabled={loading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition disabled:opacity-40"
        >
          <X size={22} />
        </button>

        {/* Logo */}
        <div className="flex items-center justify-center mb-6">
          <img src={logo} alt="Revibe logo" className="h-9" />
        </div>

        <h2 className="text-lime-500 text-xl font-bold text-center mb-1">
          Enter Referral Code
        </h2>
        <p className="text-gray-500 text-sm text-center mb-6">
          Both you and your friend get rewarded!
        </p>

        {/* Input */}
        <div
          className="flex items-center gap-3 rounded-2xl px-4 py-3 mb-2"
          style={{
            border: `1.5px solid ${error ? "#f87171" : "#d1d5db"}`,
            backgroundColor: "#f9fafb",
          }}
        >
          <Gift size={20} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Enter code (e.g. JAREB0003)"
            value={referralCode}
            onChange={(e) => {
              setReferralCode(e.target.value.toUpperCase());
              if (error) setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && !loading && handleApply()}
            disabled={loading || applied}
            className="flex-1 bg-transparent text-gray-800 placeholder-gray-400 text-base focus:outline-none disabled:opacity-60 uppercase"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-xs text-center mb-3 px-1">{error}</p>
        )}

        {/* Success */}
        {applied && !error && (
          <p className="text-green-500 text-sm text-center mb-3 font-semibold flex items-center justify-center gap-1">
            <CheckCircle2 size={16} />
            {referrerName
              ? `Code from ${referrerName} applied! You saved ₹${discountAmount}`
              : `Referral applied! You saved ₹${discountAmount}`}
          </p>
        )}

        {/* Spacer */}
        {!error && !applied && <div className="mb-3" />}

        {/* Apply button */}
        <button
          onClick={handleApply}
          disabled={loading || applied}
          className="w-full py-4 bg-lime-400 hover:bg-lime-500 text-black font-bold rounded-full transition duration-300 mb-4 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Validating…
            </>
          ) : (
            "Apply Referral"
          )}
        </button>

        <button
          onClick={handleClose}
          disabled={loading}
          className="w-full text-center text-gray-500 text-sm hover:text-gray-700 transition disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ReferralModal;